import { Link } from 'react-router-dom';
import { X, Search, ChevronRight, ChevronDown, ChevronLeft, User, Heart, Eye, Users, MessageCircle, Bookmark, Play, Music, UserPlus, UsersRound, Smile, Repeat, ThumbsUp } from 'lucide-react';
import { useState, useMemo } from 'react';
import SocialIcon from '@/components/SocialIcon';
import { PRODUCT_KEYWORDS, getProductFlagData, ProductFlag, getKeywordForProduct } from '@/components/CategoryMegaMenu';
import { useLanguage } from '@/hooks/useLanguage';
import { useStorefront, SINGLE_LANGUAGE_STOREFRONTS } from '@/hooks/useStorefront';
import { useTranslations } from '@/hooks/useTranslations';
import type { Tables } from '@/integrations/supabase/types';
import type { DBProduct } from '@/hooks/useProducts';

const MOBILE_KEYWORD_ICONS: Record<string, React.ReactNode> = {
  'Follower': <Users className="h-5 w-5" />,
  'Likes': <Heart className="h-5 w-5" />,
  'Views': <Eye className="h-5 w-5" />,
  'Story Views': <Eye className="h-5 w-5" />,
  'Kommentare': <MessageCircle className="h-5 w-5" />,
  'Saves': <Bookmark className="h-5 w-5" />,
  'Abonnenten': <UserPlus className="h-5 w-5" />,
  'Subscriber': <UserPlus className="h-5 w-5" />,
  'Streams': <Play className="h-5 w-5" />,
  'Hörer': <Music className="h-5 w-5" />,
  'Plays': <Play className="h-5 w-5" />,
  'Shares': <Repeat className="h-5 w-5" />,
  'Reposts': <Repeat className="h-5 w-5" />,
  'Reaktionen': <Smile className="h-5 w-5" />,
  'Mitglieder': <UsersRound className="h-5 w-5" />,
  'Gruppenmitglieder': <UsersRound className="h-5 w-5" />,
  'Wachstum': <Users className="h-5 w-5" />,
  'Page': <ThumbsUp className="h-5 w-5" />,
  'Profil': <UserPlus className="h-5 w-5" />,
};

type Category = Tables<'categories'>;

const languages = [
  { code: 'de', label: 'German', flag: '🇨🇭', flagCode: 'ch' },
  { code: 'en', label: 'English', flag: '🇺🇸', flagCode: 'us' },
  { code: 'it', label: 'Italian', flag: '🇮🇹', flagCode: 'it' },
  { code: 'fr', label: 'French', flag: '🇫🇷', flagCode: 'fr' },
];

interface ProductGroup {
  keyword: string;
  products: DBProduct[];
}

function groupProductsByKeyword(products: DBProduct[]): ProductGroup[] {
  const groups = new Map<string, DBProduct[]>();
  const ungrouped: DBProduct[] = [];

  for (const product of products) {
    let matched = false;
    for (const keyword of PRODUCT_KEYWORDS) {
      if (product.title.toLowerCase().includes(keyword.toLowerCase())) {
        if (!groups.has(keyword)) groups.set(keyword, []);
        groups.get(keyword)!.push(product);
        matched = true;
        break;
      }
    }
    if (!matched) ungrouped.push(product);
  }

  const result: ProductGroup[] = [];
  for (const [keyword, prods] of groups) {
    if (prods.length >= 2) {
      result.push({ keyword, products: prods });
    } else {
      ungrouped.push(...prods);
    }
  }
  if (ungrouped.length > 0) {
    result.push({ keyword: '', products: ungrouped });
  }
  return result;
}

/** Get display title: translated if available, else original */
function dt(product: DBProduct, overrides?: Record<string, string>): string {
  return overrides?.[product.id] || product.title;
}

/** Get link slug: translated if available, else original */
function ls(product: DBProduct, overrides?: Record<string, string>): string {
  return overrides?.[product.id] || product.slug;
}

type DrillLevel =
  | { level: 0 }
  | { level: 1; category: Category }
  | { level: 2; category: Category; group: ProductGroup };

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  /** Raw (German) products for grouping/flag logic */
  products: DBProduct[];
  /** productId → translated title */
  titleOverrides?: Record<string, string>;
  /** productId → translated slug */
  slugOverrides?: Record<string, string>;
  currentLang: typeof languages[0];
  onLangChange: (lang: typeof languages[0]) => void;
}

export default function MobileMenu({ open, onClose, categories, products, titleOverrides, slugOverrides, currentLang, onLangChange }: MobileMenuProps) {
  const { localePath } = useLanguage();
  const { storefront } = useStorefront();
  const { t } = useTranslations();
  const [search, setSearch] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const [drill, setDrill] = useState<DrillLevel>({ level: 0 });

  const filteredCategories = search.trim()
    ? categories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : categories;

  const getCategoryProducts = (categoryId: string) =>
    products.filter(p => p.category_id === categoryId);

  const goBack = () => {
    if (drill.level === 2) setDrill({ level: 1, category: drill.category });
    else if (drill.level === 1) setDrill({ level: 0 });
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => setDrill({ level: 0 }), 300);
  };

  const headerTitle = drill.level === 0
    ? null
    : drill.level === 1
      ? drill.category.name
      : `${drill.category.name} ${drill.group.keyword}`;

  return (
    <>
      <div
        className={`fixed inset-0 z-[1290] bg-black/50 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={handleClose}
      />

      <div
        className={`fixed inset-y-0 left-0 z-[1300] flex w-[85%] max-w-[360px] flex-col bg-background shadow-2xl transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          {drill.level === 0 ? (
            SINGLE_LANGUAGE_STOREFRONTS.includes(storefront) ? (
              // Single-Language-Storefronts (AT/IT/AL/…) — kein Umschalter, nur
              // Platzhalter, damit das flex-Layout (justify-between) stabil bleibt.
              <div />
            ) : (
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent"
              >
                <img src={`/flags/${currentLang.flagCode.toUpperCase()}.svg`} width="20" height="15" alt={currentLang.label} className="inline-block rounded-[2px]" />
                <span>{currentLang.label}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute left-0 top-full z-10 mt-1 w-44 rounded-xl border bg-card p-1 shadow-lg">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => { onLangChange(lang); setLangOpen(false); }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent"
                    >
                      <img src={`/flags/${lang.flagCode.toUpperCase()}.svg`} width="20" height="15" alt={lang.label} className="inline-block rounded-[2px]" />
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            )
          ) : (
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-sm font-semibold text-foreground"
            >
              <ChevronLeft className="h-5 w-5" />
              {headerTitle}
            </button>
          )}

          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Menü schliessen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {drill.level === 0 && (
          <div className="border-b px-4 py-3">
            <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
              <input
                type="text"
                placeholder={t('general.search', 'Search Product')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {drill.level === 0 && (
            <nav className="divide-y">
              {filteredCategories.map(cat => {
                const catProducts = getCategoryProducts(cat.id);
                const hasProducts = catProducts.length > 0;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      if (hasProducts) {
                        setDrill({ level: 1, category: cat });
                      }
                    }}
                    className="flex w-full items-center justify-between px-4 py-3.5 text-sm font-medium transition-colors hover:bg-accent"
                  >
                    <div className="flex items-center gap-3">
                      <SocialIcon slug={cat.slug} className="h-5 w-5 text-muted-foreground" />
                      <span>{cat.name}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                );
              })}
            </nav>
          )}

          {drill.level === 1 && (
            <Level1View
              category={drill.category}
              products={getCategoryProducts(drill.category.id)}
              titleOverrides={titleOverrides}
              slugOverrides={slugOverrides}
              onSelectGroup={(group) => setDrill({ level: 2, category: drill.category, group })}
              onClose={handleClose}
            />
          )}

          {drill.level === 2 && (
            <Level2View
              products={drill.group.products}
              titleOverrides={titleOverrides}
              slugOverrides={slugOverrides}
              onClose={handleClose}
            />
          )}
        </div>

        {drill.level === 0 && (
          <div className="border-t">
            <Link
              to={localePath('/konto')}
              onClick={handleClose}
              className="flex items-center justify-between px-4 py-3.5 text-sm font-medium transition-colors hover:bg-accent"
            >
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground" />
                <span>{t('nav.account', 'Mein Konto')}</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          </div>
        )}
      </div>
    </>
  );
}

function ProductLeadingIcon({ title }: { title: string }) {
  const flagData = getProductFlagData(title);
  if (flagData) return <ProductFlag title={title} />;
  const keyword = getKeywordForProduct(title);
  const icon = MOBILE_KEYWORD_ICONS[keyword];
  if (icon) return <span className="text-muted-foreground shrink-0">{icon}</span>;
  return <span className="inline-block w-5 shrink-0" aria-hidden="true" />;
}

function Level1View({
  category,
  products,
  titleOverrides,
  slugOverrides,
  onSelectGroup,
  onClose,
}: {
  category: Category;
  products: DBProduct[];
  titleOverrides?: Record<string, string>;
  slugOverrides?: Record<string, string>;
  onSelectGroup: (group: ProductGroup) => void;
  onClose: () => void;
}) {
  const { localePath } = useLanguage();
  const groups = useMemo(() => groupProductsByKeyword(products), [products]);
  const hasNamedGroups = groups.some(g => g.keyword);

  if (!hasNamedGroups) {
    return (
      <nav className="divide-y">
        {products.map(product => (
          <Link
            key={product.id}
            to={localePath(`/produkt/${ls(product, slugOverrides)}`)}
            onClick={onClose}
            className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium transition-colors hover:bg-accent"
          >
            <ProductLeadingIcon title={product.title} />
            <span>{dt(product, titleOverrides)}</span>
          </Link>
        ))}
      </nav>
    );
  }

  return (
    <nav className="divide-y">
      <Link
        to={localePath(`/${category.slug}`)}
        onClick={onClose}
        className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-primary transition-colors hover:bg-accent"
      >
        <SocialIcon slug={category.slug} className="h-5 w-5" />
        <span>{category.name}</span>
      </Link>

      {groups.map((group) => {
        if (group.keyword) {
          if (group.products.length > 1) {
            return (
              <button
                key={group.keyword}
                onClick={() => onSelectGroup(group)}
                className="flex w-full items-center justify-between px-4 py-3.5 text-sm font-medium transition-colors hover:bg-accent"
              >
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground shrink-0">{MOBILE_KEYWORD_ICONS[group.keyword]}</span>
                  <span>{category.name} {group.keyword}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {group.products.length}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            );
          }

          return (
            <Link
              key={group.keyword}
              to={localePath(`/produkt/${ls(group.products[0], slugOverrides)}`)}
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium transition-colors hover:bg-accent"
            >
              <span className="text-muted-foreground shrink-0">{MOBILE_KEYWORD_ICONS[group.keyword]}</span>
              <span>{dt(group.products[0], titleOverrides)}</span>
            </Link>
          );
        }

        return group.products.map(product => (
          <Link
            key={product.id}
            to={localePath(`/produkt/${ls(product, slugOverrides)}`)}
            onClick={onClose}
            className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium transition-colors hover:bg-accent"
          >
            <ProductLeadingIcon title={product.title} />
            <span>{dt(product, titleOverrides)}</span>
          </Link>
        ));
      })}
    </nav>
  );
}

function Level2View({
  products,
  titleOverrides,
  slugOverrides,
  onClose,
}: {
  products: DBProduct[];
  titleOverrides?: Record<string, string>;
  slugOverrides?: Record<string, string>;
  onClose: () => void;
}) {
  const { localePath } = useLanguage();
  return (
    <nav className="divide-y">
      {products.map(product => {
        const flagData = getProductFlagData(product.title);
        return (
          <Link
            key={product.id}
            to={localePath(`/produkt/${ls(product, slugOverrides)}`)}
            onClick={onClose}
            className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium transition-colors hover:bg-accent"
          >
            {flagData && <ProductFlag title={product.title} />}
            {!flagData && (
              <span className="text-muted-foreground">
                {MOBILE_KEYWORD_ICONS[getKeywordForProduct(product.title)] || <span className="inline-block w-5" />}
              </span>
            )}
            <span>{dt(product, titleOverrides)}</span>
          </Link>
        );
      })}
    </nav>
  );
}
